import React, { useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Box,
  CircularProgress,
} from "@mui/material";
import { storage, db, functions } from "../../configs/firebaseConfig";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, addDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import QRCode from "qrcode";
import { httpsCallable } from "firebase/functions";
import companies from "../../configs/companies";
import { buildBusinessCardUrl } from "../../utils/businessCardUrl";

// Helpers
const pickDefined = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );

const defaultFormFields = {
  name: { show: true, required: true },
  position: { show: true, required: true },
  bio: { show: true, required: true },
  phone: { show: true, required: true },
  email: { show: true, required: true },
  profileUrl: { show: false, required: false }, // off by default; enable per company
  address: { show: true, required: true },
  profilePic: { show: true, required: false },
  baseLocation: { show: false, required: false },
  company: { show: false, required: false },
  department: { show: false, required: false },
  experience: { show: false, required: false },
  languages: { show: false, required: false },
  mission: { show: false, required: false },
  vision: { show: false, required: false },
};

const BusinessCardForm = ({ onAddCard, companyName }) => {
  const companyConfig = companies[companyName] || companies.default;

  // Merge company overrides with defaults
  const formFields = {
    ...defaultFormFields,
    ...(companyConfig.formFields || {}),
  };

  const [formData, setFormData] = useState({
    name: "",
    position: "",
    bio: "",
    phone: "",
    email: "",
    profileUrl: "",
    address: "",
    profilePic: null,
    baseLocation: "",
    company: "",
    department: "",
    experience: "",
    languages: "",
    mission: "",
    vision: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  // Handle image upload selection
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    setFormData((s) => ({ ...s, profilePic: file || null }));
  };

  // Upload QR to storage
  const uploadQRCode = async (qrCodeDataURL) => {
    const qrCodeBlob = await fetch(qrCodeDataURL).then((r) => r.blob());
    const storageRefQR = ref(
      storage,
      `${companyName}/qrCodes/${formData.email}-qrcode.png`
    );
    const snapshot = await uploadBytes(storageRefQR, qrCodeBlob);
    return await getDownloadURL(snapshot.ref);
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const auth = getAuth();
      if (!auth.currentUser)
        throw new Error("You must be logged in to upload files.");

      let profilePicURL = "";

      if (formFields.profilePic.show && formData.profilePic) {
        const storageRefPic = ref(
          storage,
          `${companyName}/profilePics/${formData.profilePic.name}`
        );
        const snapshot = await uploadBytes(storageRefPic, formData.profilePic);
        profilePicURL = await getDownloadURL(snapshot.ref);
      }

      // Build payload only with fields that are enabled
      const payload = pickDefined({
        name: formFields.name.show ? formData.name.trim() : undefined,
        position: formFields.position.show
          ? formData.position.trim()
          : undefined,
        bio: formFields.bio.show ? formData.bio.trim() : undefined,
        phone: formFields.phone.show ? formData.phone.trim() : undefined,
        email: formFields.email.show ? formData.email.trim() : undefined,
        profileUrl: formFields.profileUrl.show
          ? formData.profileUrl.trim()
          : undefined,
        address: formFields.address.show ? formData.address.trim() : undefined,
        profilePicURL: formFields.profilePic.show ? profilePicURL : undefined,
        baseLocation: formFields.baseLocation?.show ? formData.baseLocation.trim() : undefined,
        companyNameText: formFields.company?.show ? formData.company.trim() : undefined,
        department: formFields.department?.show ? formData.department.trim() : undefined,
        experience: formFields.experience?.show ? formData.experience.trim() : undefined,
        languages: formFields.languages?.show ? formData.languages.trim() : undefined,
        mission: formFields.mission?.show ? formData.mission.trim() : undefined,
        vision: formFields.vision?.show ? formData.vision.trim() : undefined,
        company: companyName,
      });

      // Basic presence validation for required fields (extra safety)
      Object.entries(formFields).forEach(([key, cfg]) => {
        if (cfg.show && cfg.required && !payload[key]) {
          throw new Error(`Please provide a valid value for "${key}".`);
        }
      });

      // Save in Firestore
      const docRef = await addDoc(collection(db, "businessCards"), payload);

      // Generate card URL & QR
      const businessCardURL = buildBusinessCardUrl(companyName, docRef.id);
      const generatedQrCodeURL = buildBusinessCardUrl(
        companyName,
        docRef.id,
        "qr"
      );
      const qrCodeDataURL = await QRCode.toDataURL(generatedQrCodeURL);
      const qrCodeDownloadURL = await uploadQRCode(qrCodeDataURL);

      // Email QR
      const sendEmailWithQRCode = httpsCallable(
        functions,
        "sendEmailWithQRCode"
      );
      await sendEmailWithQRCode({
        email: payload.email,
        qrCodeDownloadURL,
        qrCodeURL: businessCardURL,
        name: payload.name,
        senderEmail: companyConfig.emailSender,
      });

      // Notify parent
      onAddCard({
        id: docRef.id,
        ...payload,
        qrCodeURL: generatedQrCodeURL,
        businessCardURL,
      });

      alert("Business card added and email sent successfully!");
      setFormData({
        name: "",
        position: "",
        bio: "",
        phone: "",
        email: "",
        profileUrl: "",
        address: "",
        profilePic: null,
        baseLocation: "",
        company: "",
        department: "",
        experience: "",
        languages: "",
        mission: "",
        vision: "",
      });
    } catch (err) {
      console.error(
        "Error uploading/saving business card:",
        err?.message || err
      );
      setError(`Error: ${err?.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Reusable props to keep labels/inputs black
  const blackFieldProps = {
    InputLabelProps: { style: { color: "black" } },
    InputProps: { style: { color: "black" } },
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Paper
        elevation={3}
        sx={{ p: 4, backgroundColor: companyConfig.formBackground }}
      >
        <Typography variant="h5" gutterBottom>
          Add New Business Card
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {formFields.name.show && (
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  name="name"
                  fullWidth
                  variant="outlined"
                  value={formData.name}
                  onChange={handleChange}
                  required={formFields.name.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.position.show && (
              <Grid item xs={12}>
                <TextField
                  label="Position"
                  name="position"
                  fullWidth
                  variant="outlined"
                  value={formData.position}
                  onChange={handleChange}
                  required={formFields.position.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.bio.show && (
              <Grid item xs={12}>
                <TextField
                  label="Bio"
                  name="bio"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={formData.bio}
                  onChange={handleChange}
                  required={formFields.bio.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.phone.show && (
              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  name="phone"
                  fullWidth
                  variant="outlined"
                  value={formData.phone}
                  onChange={handleChange}
                  required={formFields.phone.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.email.show && (
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={formData.email}
                  onChange={handleChange}
                  required={formFields.email.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.profileUrl.show && (
              <Grid item xs={12}>
                <TextField
                  label="Profile URL"
                  name="profileUrl"
                  fullWidth
                  variant="outlined"
                  placeholder="https://example.com/team/jane-doe"
                  value={formData.profileUrl}
                  onChange={handleChange}
                  required={formFields.profileUrl.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.address.show && (
              <Grid item xs={12}>
                <TextField
                  label="Address"
                  name="address"
                  fullWidth
                  variant="outlined"
                  value={formData.address}
                  onChange={handleChange}
                  required={formFields.address.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.baseLocation?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Based In"
                  name="baseLocation"
                  fullWidth
                  variant="outlined"
                  value={formData.baseLocation}
                  onChange={handleChange}
                  required={formFields.baseLocation.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.company?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Company Name"
                  name="company"
                  fullWidth
                  variant="outlined"
                  value={formData.company}
                  onChange={handleChange}
                  required={formFields.company.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.department?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Department"
                  name="department"
                  fullWidth
                  variant="outlined"
                  value={formData.department}
                  onChange={handleChange}
                  required={formFields.department.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.experience?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Experience"
                  name="experience"
                  fullWidth
                  variant="outlined"
                  value={formData.experience}
                  onChange={handleChange}
                  required={formFields.experience.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.languages?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Languages"
                  name="languages"
                  fullWidth
                  variant="outlined"
                  value={formData.languages}
                  onChange={handleChange}
                  required={formFields.languages.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.mission?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Mission"
                  name="mission"
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  value={formData.mission}
                  onChange={handleChange}
                  required={formFields.mission.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.vision?.show && (
              <Grid item xs={12}>
                <TextField
                  label="Vision"
                  name="vision"
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  value={formData.vision}
                  onChange={handleChange}
                  required={formFields.vision.required}
                  {...blackFieldProps}
                />
              </Grid>
            )}

            {formFields.profilePic.show && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  fullWidth
                >
                  Upload Profile Picture
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                fullWidth
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Submit"}
              </Button>

              {error && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default BusinessCardForm;
